import React from "react";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { Dispatch } from "redux";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import { AbtaLogo, AstaLogo, AtolLogo, CircleDots, LinkButton, Loader } from "../../core/atoms/atoms";
import { apiPromiseHook, createEnterHookPromise } from "../../shared/flux/actions/enterHooks";
import { CollectionKeyHelper } from "../../shared/helpers/contentCollectionHelpers";
import { AvatarWithText, GoogleAnalyticsHelper, SectionHeading } from "@travellocal/ui";
import { MatchState } from "../../shared/helpers/reduxHelpers";
import { CallToActionBlock } from "../components/ctaBlock/ctaBlock";
import { FeaturedCountriesCards, featuredLocationIds } from "../components/home/featuredCountriesCards";
import { ImageCycler, ImmersiveSearch } from "../components/home/hero";
import { HomeReviews } from "../components/home/reviews/reviews";
import { HomeTripIdeas } from "../components/home/tripIdeas/tripIdeas";
import { LtoTestimonial } from "../components/ltoTestimonial";
import { SwipeCarousel } from "../components/swipeCarousel/swipeCarousel";
import { ActionPromises } from "./../state/actionPromises";
import "./home.scss";

const stateToProps = (store: IWebStoreState) => ({
  routing: store.routing,
  regionsState: store.regions,
  countriesState: store.countries,
  featuredLocationsState: store.featuredLocations,
  contentState: store.contentByCollection,
});

const dispatchToProps = (dispatch: Dispatch<IWebStoreState>) => ({
  navigateToLocation: (location: ILocationSummary) => dispatch(push(WebRoutes.location(location))),
});

type Props = ReturnType<typeof dispatchToProps> & ReturnType<typeof stateToProps>;

interface HeroImages {
  image: ISrcSet;
  meta: string;
}

const homeHeroImages: HeroImages[] = [{
  src: "hero-1",
  meta: "Person walking down street past red lanterns in Yunnan, China",
},
{
  src: "hero-2",
  meta: "Person swinging off a palm tree into the sea",
},
{
  src: "hero-3",
  meta: "Person rowing a boat on a river in a jungle",
}].map(({ src, meta }) => ({
  image: {
    default: `https://resources.travellocal.com/homepage/${src}_large.jpg`,
    srcs: [
      {
        src: `https://resources.travellocal.com/homepage/${src}_small.jpg`,
        width: 512,
      },
      {
        src: `https://resources.travellocal.com/homepage/${src}_large.jpg`,
        width: 1920,
      },
    ],
  },
  meta,
}));

const HomeComponent = ({ featuredLocationsState, countriesState }: Props) => {
  const oGImg = `${globalConfig.wwwBaseUrl}/images/homepage/opengraph.png`;
  const description = "Authentic tailor-made holidays crafted by trusted local experts worldwide. After all, who knows a country better than the people who actually live there?";
  const meta = [
    { name: "description", content: description },
    { property: "og:type", content: "website" },
    { property: "og:url", content: globalConfig.wwwBaseUrl },
    { property: "og:title", content: "Tailor-made holidays by local experts" },
    { property: "og:description", content: description },
    { property: "og:image", content: oGImg },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "599" },
  ];
  const link = [
    { rel: "canonical", href: globalConfig.wwwBaseUrl },
  ];

  return (
    <div className="home">
      <Helmet
        title="Tailor-made holidays by local experts"
        meta={meta}
        link={link}
      />

      <section className="home-hero">
        <ImageCycler images={homeHeroImages} />

        <header className="columns is-vcentered is-mobile is-marginless">
          <div className="column is-10-widescreen is-offset-1-widescreen">
            <h2 className="heading--1 text--light">A better way to book <br />your perfect holiday</h2>
            <h1 className="heading--4 text--light text--caps">Tailor-made by trusted local travel agents</h1>
          </div>
        </header>

        <ImmersiveSearch />
      </section>

      <section className="our-approach width-wrapper home__section">
        <SectionHeading
          alignment="centered"
          preTitle="A better way to travel"
          title="Our Approach"
        />
        <div className="columns">
          <div className="column is-4">
            <AvatarWithText
              heading={<>
                <span className="our-approach__avatar-heading--avoid-wrap">Local knowledge;</span>
                {" "}
                <span className="our-approach__avatar-heading--avoid-wrap">extraordinary holidays</span>
              </>}
              htmlString="We connect you with handpicked local travel experts who actually live in your destination to plan your trip. Their unbeatable local knowledge means that your holiday is guaranteed to be truly extraordinary."
              imageAlt="Our local experts"
              imageSrc="https://resources.travellocal.com/lto/a91f6620-4287-4d73-b822-8aecc1612a86-team-charleston-rwanda-LTO_regular.JPG"
              imageTitle="Our local experts"
            />
          </div>
          <div className="column is-4">
            <AvatarWithText
              heading="Our Peace of Mind Guarantee"
              htmlString="Book your holiday with complete peace of mind thanks to our <a href='https://go.travellocal.com/book-with-confidence' target='_blank' rel='noopener noreferrer'>free cancellation and hassle-free postponement terms</a>. We’ve also been listed by <a href='https://www.which.co.uk/news/2020/10/small-holiday-companies-pay-refunds-while-multinationals-stall/' target='_blank' rel='noopener noreferrer'>Which?</a> as a company you can trust with your money, even during a pandemic."
              imageAlt="Peace of Mind"
              imageSrc="https://resources.travellocal.com/peace-of-mind.png"
              imageTitle="Peace of Mind"
            />
          </div>
          <div className="column is-4">
            <AvatarWithText
              heading="Thoughtful travel"
              htmlString="We’ve chosen our local partners because we know they create the best tailor-made holidays. And, since they live in your destination, more of your money goes into their economy and to supporting their local communities."
              imageAlt="Our local partners"
              imageSrc="https://resources.travellocal.com/lto/7387d638-52d3-43c9-86f1-18002a7a493f-team-oku-japan-lto_regular.jpg"
              imageTitle="Our local partners"
            />
          </div>
        </div>
      </section>

      <section className="featured-countries home__section home__section--no-x-padding">
        <div className="width-wrapper">
          <h2 className="heading--2">Explore some of our top destinations</h2>
          {MatchState(featuredLocationsState, {
            none: err => err
              ? <p>Sorry, something went wrong: {err.message}</p>
              : <Loader />,
            data: locations => <FeaturedCountriesCards locations={locations} />,
          })}

          <div className="columns is-centered is-mobile">
            {MatchState(countriesState, {
              data: countries => (
                <LinkButton
                  to={WebRoutes.destinations()}
                  buttonClass="primary-button"
                  className="heading--4 text--light">
                  Explore
                </LinkButton>
              )
            })}
          </div>
        </div>
      </section>

      <div className="lto-testimonials-wrap width-wrapper home__section">
        <div className="columns is-centered has-text-centered is-mobile is-marginless">
          <div className="home-trip-ideas__copy column is-full-mobile is-10-tablet is-7-desktop is-paddingless">
            <h2 className="heading--2">The best local agents, handpicked for you</h2>
            <p className="text--regular">
              We work hard to find the best local travel agents -
              people who know their country better than anyone because
              they live there. We connect you directly with them, so you
              get a better holiday and you both get a fairer deal.
            </p>
          </div>
        </div>

        <SwipeCarousel slickSettings={{ slidesToShow: 1 }}>
          <LtoTestimonial
            quote="Poly has been welcoming travellers to Brazil since 2011. We chose to partner with her agency because she stood out from the crowd in providing the very best bespoke trips to her clients."
            imageLeft="/images/ltos/homepage/tailor-made-safaris_regular.jpg"
            avatarImage="/images/ltos/homepage/viare_regular.jpg"
            imageRight="/images/ltos/homepage/tonkin_regular.jpeg"
            quotee="Poly"
            country={{ name: "Brazil", slug: "brazil", region: "south-america" }}
            onClick={() => GoogleAnalyticsHelper.logEvent("Select Country", "Click", "LTO Testimonial")}
          />

          <LtoTestimonial
            quote="Hue and Ly and the rest of their team know Vietnam like no other local travel agents we’ve found. Their on-the-ground knowledge really does set them apart from the rest."
            imageLeft="/images/ltos/homepage/viare_regular.jpg"
            avatarImage="/images/ltos/homepage/tonkin_regular.jpeg"
            imageRight="/images/ltos/homepage/tailor-made-safaris_regular.jpg"
            quotee="Hue and Ly"
            country={{ name: "Vietnam", slug: "vietnam", region: "asia" }}
            onClick={() => GoogleAnalyticsHelper.logEvent("Select Country", "Click", "LTO Testimonial")}
          />

          <LtoTestimonial
            quote="Nick and his team in South Africa have an incredible wealth of knowledge when it comes to the nature and wildlife of their diverse country. They can organise wildlife tours that go into far more depth than your average safari company - but you’ll still get all the highlights!"
            imageLeft="/images/ltos/homepage/tonkin_regular.jpeg"
            avatarImage="/images/ltos/homepage/tailor-made-safaris_regular.jpg"
            imageRight="/images/ltos/homepage/viare_regular.jpg"
            quotee="Nick"
            country={{ name: "South Africa", slug: "south-africa", region: "africa" }}
            onClick={() => GoogleAnalyticsHelper.logEvent("Select Country", "Click", "LTO Testimonial")}
          />
        </SwipeCarousel>
      </div>

      <HomeReviews />

      <HomeTripIdeas />

      <section className="press-logos-wrap home__section">
        <div className="press-logos">
          <h3>We’re proud to have featured in:</h3>
          <div className="columns is-centered is-multiline is-mobile">
            <div className="home-logo column is-2-desktop">
              <img
                className="lazyload"
                data-src="images/homepage/affiliates/national-geographic.svg"
                alt="National Geographic"
              />
            </div>
            <div className="home-logo column is-2-desktop">
              <img
                className="lazyload"
                data-src="images/homepage/affiliates/the-guardian.png"
                alt="The Guardian"
              />
            </div>
            <div className="home-logo column is-2-desktop">
              <img
                className="lazyload"
                data-src="images/homepage/affiliates/the-telegraph.png"
                alt="The Daily Telegraph"
              />
            </div>
            <div className="home-logo column is-2-desktop">
              <img
                className="lazyload"
                data-src="images/homepage/affiliates/the-times.png"
                alt="The Times"
              />
            </div>
            <div className="home-logo column is-2-desktop">
              <img
                className="lazyload"
                data-src="images/homepage/affiliates/the-independent.png"
                alt="The Independent"
              />
            </div>
          </div>          
        </div>
      </section>

      <section className="inspiration-cta-wrap is-centered">
        <CallToActionBlock
          linkUrl={WebRoutes.inspiration()}
          linkText="Start your journey"
          bodyTitle="Inspiration from around the world"
        >
          <div>
            <p className="home-insp__text text--regular">
              Looking for inspiration? We’ve worked with our local agents to bring you a
              wealth of articles and trip ideas from around the world.
            </p>
            <p className="home-insp__text text--regular">
              Have a browse… and discover why local knowledge will make a world of difference to your holiday.
            </p>
          </div>
        </CallToActionBlock>
      </section>

      <section className="book-with-confidence-wrap home__section">
        <div className="book-with-confidence">
          <h3>Book with confidence</h3>
          <div className="columns is-centered is-vcentered is-mobile is-multiline">
            <div className="column is-narrow">
              <a href="http://abta.com/tips-and-latest/travel-with-confidence" target="_blank" rel="noopener noreferrer">
                <AbtaLogo type="colour" height={55} />
              </a>
            </div>
            <div className="column is-narrow">
              <a href="https://www.caa.co.uk/ATOL-protection/Consumers/About-ATOL/" target="_blank" rel="noopener noreferrer">
                <AtolLogo type="dark" height={80} />
              </a>
            </div>
            <div className="column is-narrow">
              <a href="https://www.asta.org/About/index.cfm" target="_blank" rel="noopener noreferrer">
                <AstaLogo type="colour" height={55} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export const Home = connect(stateToProps, dispatchToProps)(HomeComponent);

export const homeOnEnter = apiPromiseHook(
  createEnterHookPromise<IWebStoreState>((router, state) => {

    const actions: Redux.Action[] = [
      ActionPromises.getFeaturedLocations(featuredLocationIds),
    ];

    const { homeReviews, homeItineraries, homeBlogs } = state.contentByCollection.data;

    if (!homeReviews || !homeReviews.data || homeReviews.data.length < 1) {
      // We only want four-star and five-star reviews to appear here
      const overallRating: INumericRange = {
        type: NumericRangeType.GreaterThanOrEqual,
        min: 4,
      };

      const context = { key: CollectionKeyHelper.HomeReviews };
      actions.push(ActionPromises.getContentPage({ types: [ContentType.Review], overallRating }, -1, 5, context));
    }

    if (!homeItineraries || !homeItineraries.data || homeItineraries.data.length < 1) {
      const context = { key: CollectionKeyHelper.HomeItineraries };
      actions.push(ActionPromises.getContentPage({ types: [ContentType.SampleItinerary] }, -1, 4, context));
    }

    if (!homeBlogs || !homeBlogs.data || homeBlogs.data.length < 1) {
      const context = { key: CollectionKeyHelper.HomeArticles };
      actions.push(ActionPromises.getContentPage({ types: [ContentType.BlogPost] }, -1, 1, context));
    }

    return actions;
  }),
);
